import React, { useState, useRef } from 'react';
import axios from 'axios';
import useSWR from 'swr';
import { TAMPIL_STATUS, rentangBulan, teksSel, ambilKehadiranCabang, hitungJamKerja } from '../../../utils/rekapPresensi';
import jsPDF from 'jspdf';
import "jspdf-autotable";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  LinearProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  Collapse,
  Container,
  CardHeader,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  CloudDownload as CloudDownloadIcon,
  CalendarToday as CalendarIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import '../../../componen/css/Dashboard.css';
import { useSelector } from 'react-redux';
import { apiHost } from '../../../utils/apiHost';


ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  ChartTooltip,
  Legend
);

axios.defaults.withCredentials = true;

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const fetcher = async (url) => {
  try {
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const formatTime = (hours, minutes) => {
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const ContentContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    width: '100%',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '70vh',
  marginTop: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '0.4em',
    height: '0.4em',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.main,
    borderRadius: '4px',
  },
}));

const StickyTableCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  background: theme.palette.background.paper,
  zIndex: 2,
  boxShadow: '2px 0px 3px rgba(0,0,0,0.1)',
}));

const SubtleCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
  borderRadius: theme.shape.borderRadius * 1.5,
  transition: 'transform 0.2s, box-shadow 0.2s',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  },
}));

const CardHeaderStyled = styled(CardHeader)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '& .MuiCardHeader-title': {
    fontWeight: 600,
  },
}));

const ExportButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  marginTop: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  padding: theme.spacing(1, 2),
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
}));

const DatePickerWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(3),
}));

const DataPresensiBulan = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [bulan, setBulan] = useState('');
  const [tahun, setTahun] = useState('');
  const [fetchData, setFetchData] = useState(false);
  const [warning, setWarning] = useState('');
  const [expandedCabang, setExpandedCabang] = useState({});
  const scrollRef = useRef(null);

  const { data: absensi, error, isLoading } = useSWR(
    fetchData ? `${getApiBaseUrl()}/absensicabang/get?bulan=${bulan}&tahun=${tahun}` : null,
    fetcher
  );

  // Status tiap hari (Hadir, Izin, Sakit, Cuti, Alfa, Libur, ...) dari Pengaturan Absensi: kolom "Status" di tiap tanggal.
  const { data: barisStatus } = useSWR(
    fetchData && bulan && tahun ? `status-bulanan:${bulan}:${tahun}` : null,
    () => { const r = rentangBulan(Number(tahun), Number(bulan)); return ambilKehadiranCabang(axios, getApiBaseUrl(), r.dari, r.sampai); },
    { revalidateOnFocus: false }
  );
  const petaStatus = {};
  (barisStatus || []).forEach((b) => { petaStatus[b.username] = b.hari; });
  // Shift karyawan pada tanggal itu (patokan jam selesai untuk lembur); kosong bila tanpa jadwal / belum termuat.
  // Teks status satu hari untuk ekspor (Hadir, Izin, ...); '-' bila tanpa status / belum termuat.
  const statusUntuk = (username, tanggal) => TAMPIL_STATUS[petaStatus[username]?.[Number(String(tanggal).slice(8, 10))]?.status]?.label || '-';
  const shiftUntuk = (username, tanggal) => petaStatus[username]?.[Number(String(tanggal).slice(8, 10))]?.shift || null;

  const extractDates = (data) => {
    const dateSet = new Set();
    Object.keys(data).forEach((key) => {
      data[key].absensi.forEach((absensi) => {
        dateSet.add(absensi.tgl_absensi);
      });
    });
    return Array.from(dateSet).sort();
  };

  const dates = absensi ? extractDates(absensi) : [];

  const sortedCabangKeys = absensi ? Object.keys(absensi).sort((a, b) => {
    const cabangA = absensi[a].cabang?.namacabang.toLowerCase() || 'Tidak ada data';
    const cabangB = absensi[b].cabang?.namacabang.toLowerCase() || 'Tidak ada data';
    return cabangA.localeCompare(cabangB);
  }) : [];

  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split("-");
    setBulan(month);
    setTahun(year);
  };

  const handleTampilkanData = () => {
    if (!bulan || !tahun) {
      setWarning('Silahkan pilih bulan dan tahun.');
    } else {
      setWarning('');
      setFetchData(true);
    }
  };

  const toggleCabangExpand = (cabangId) => {
    setExpandedCabang(prev => ({
      ...prev,
      [cabangId]: !prev[cabangId]
    }));
  };

  const handleExportPDF = (cabangId) => {
    const cabang = absensi[cabangId];
    const doc = new jsPDF({ orientation: 'landscape' });
    const chunkSize = 5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    const cabangNamePDF = cabang.absensi[0]?.User.Cabang?.namacabang || 'Tidak Diketahui';
    doc.text(`Absensi PT.Apalah ${cabangNamePDF}`, 14, 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Periode: ${bulan}/${tahun}`, 14, 18);
    let startYPosition = 25;
    
    for (let i = 0; i < dates.length; i += chunkSize) {
      const dateChunk = dates.slice(i, i + chunkSize);
      const headerRow1 = ["Nama", ...dateChunk.map(date => ({
        content: new Date(date).toLocaleDateString('id-ID'),
        colSpan: 5,
        styles: { halign: 'center' }
      }))];

      const headerRow2 = ["", ...dateChunk.flatMap(() => ["Jam Masuk", "Jam Keluar", "Total Jam", "Lembur", "Status"])];
      const tableRows = [];

      cabang.absensi.reduce((acc, record) => {
        const existingRow = acc.find(row => row.nama === record.User.username);
        if (existingRow) {
          existingRow.data[record.tgl_absensi] = {
            jamMasuk: record.jam_masuk || 'Libur',
            jamKeluar: record.jam_keluar || ''
          };
        } else {
          acc.push({
            nama: record.User.username,
            data: {
              [record.tgl_absensi]: {
                jamMasuk: record.jam_masuk || 'Libur',
                jamKeluar: record.jam_keluar || ''
              }
            }
          });
        }
        return acc;
      }, []).forEach(row => {
        const rowData = [row.nama];
        dateChunk.forEach(date => {
          const jamMasuk = row.data[date]?.jamMasuk || '-';
          const jamKeluar = row.data[date]?.jamKeluar || '-';
          
          let totalJam = '-';
          let lembur = '-';
          
          if (jamMasuk !== '-' && jamKeluar !== '-') {
            const workingTime = hitungJamKerja(jamMasuk, jamKeluar, shiftUntuk(row.nama, date));
            totalJam = `${workingTime.totalHours}:${workingTime.totalMinutes.toString().padStart(2, '0')}`;
            
            if (workingTime.overtime.hours > 0 || workingTime.overtime.minutes > 0) {
              lembur = `${workingTime.overtime.hours}:${workingTime.overtime.minutes.toString().padStart(2, '0')}`;
            }
          }
          
          rowData.push(jamMasuk, jamKeluar, totalJam, lembur, statusUntuk(row.nama, date));
        });
        tableRows.push(rowData);
      });
      
      if (startYPosition + 10 > doc.internal.pageSize.height - 30) {
        doc.addPage();
        startYPosition = 20;
      }
      
      doc.autoTable({
        head: [headerRow1, headerRow2],
        body: tableRows,
        startY: startYPosition,
        theme: 'grid',
        styles: {
          fontSize: 8,
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: [255, 255, 255],
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.5,
          lineColor: [220, 220, 220]
        },
        bodyStyles: {
          lineWidth: 0.5,
          lineColor: [220, 220, 220]
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });

      startYPosition = doc.previousAutoTable.finalY + 10;
    }

    const pageWidth = doc.internal.pageSize.getWidth();

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });

    if (startYPosition + 50 > doc.internal.pageSize.height - 30) {
      doc.addPage();
      startYPosition = 20;
    }

    startYPosition += 20;
    doc.text(`Semarang, ${formattedDate}`, pageWidth - 100, startYPosition);

    startYPosition += 25;
    doc.text('_____________', pageWidth - 80, startYPosition);
    doc.text(' Nama ', pageWidth - 80, startYPosition + 10);

    doc.save(`export-${cabang.cabang?.namacabang || cabang.absensi[0]?.User?.Cabang?.namacabang || 'Tidak Diketahui'}-${bulan}-${tahun}.pdf`);
  };

  const handleExportExcel = async (cabangId) => {
    try {
      const cabangData = absensi[cabangId];
      const enhancedData = {
        ...cabangData,
        absensi: cabangData.absensi.map(record => {
          const workingTime = record.jam_masuk && record.jam_keluar 
            ? hitungJamKerja(record.jam_masuk, record.jam_keluar, shiftUntuk(record.User?.username, record.tgl_absensi)) 
            : { totalHours: 0, totalMinutes: 0, overtime: { hours: 0, minutes: 0 } };
            
          return {
            ...record,
            statusHari: statusUntuk(record.User?.username, record.tgl_absensi),
            totalJamKerja: record.jam_masuk && record.jam_keluar 
              ? `${workingTime.totalHours}:${workingTime.totalMinutes.toString().padStart(2, '0')}` 
              : '-',
            lembur: (workingTime.overtime.hours > 0 || workingTime.overtime.minutes > 0)
              ? `${workingTime.overtime.hours}:${workingTime.overtime.minutes.toString().padStart(2, '0')}`
              : '-'
          };
        })
      };
      
      const response = await axios.post(
        `${getApiBaseUrl()}/export/excel`,
        { data: enhancedData },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cabangNameExcel = absensi[cabangId].absensi[0]?.User.Cabang.namacabang || 'Tidak Diketahui';
      link.setAttribute('download', `export-${cabangNameExcel}-${bulan}-${tahun}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error exporting Excel:', error);
    }
  };

  const formatWithLeadingZero = (num) => {
    return num < 10 ? `0${num}` : num;
  };

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = formatWithLeadingZero(today.getMonth() + 1);
  const defaultDateValue = `${currentYear}-${currentMonth}`;

  return (
    <ContentContainer>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 3,
          background: 'linear-gradient(to right, #f5f7ff, #ffffff)',
          border: '1px solid #e0e6ff'
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom fontWeight={600} color="primary">
          Data Absensi Bulanan
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Silahkan pilih bulan dan tahun untuk melihat data absensi karyawan
        </Typography>

        <DatePickerWrapper>
          <TextField
            label="Pilih Bulan dan Tahun"
            type="month"
            value={bulan && tahun ? `${tahun}-${bulan}` : ''}
            onChange={handleMonthChange}
            defaultValue={defaultDateValue}
            InputLabelProps={{ shrink: true }}
            fullWidth={isMobile}
            size="small"
            sx={{ maxWidth: isMobile ? '100%' : '300px' }}
            InputProps={{
              startAdornment: <CalendarIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            }}
          />
          <StyledButton
            variant="contained"
            color="primary"
            disableElevation
            onClick={handleTampilkanData}
            startIcon={<SearchIcon />}
            disabled={!bulan || !tahun}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
          >
            Tampilkan Data
          </StyledButton>
        </DatePickerWrapper>

        {warning && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {warning}
          </Alert>
        )}

        {fetchData && isLoading && (
          <Box sx={{ width: '100%', mt: 4, mb: 4 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }} align="center">
              Memuat data absensi...
            </Typography>
          </Box>
        )}

        {fetchData && error && (
          <Alert 
            severity="info" 
            sx={{ mb: 3, borderRadius: 2 }}
          >
            Data kosong untuk bulan {bulan} dan tahun {tahun}.
          </Alert>
        )}

        {sortedCabangKeys.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 500 }}>
              Menampilkan data untuk periode: {bulan}/{tahun}
            </Typography>
            
            {sortedCabangKeys.map((key) => {
              const cabang = absensi[key];
              const isExpanded = expandedCabang[key] !== false;
              
              return (
                <SubtleCard key={key}>
                  <CardHeaderStyled
                    title={cabang.absensi[0]?.User?.Cabang?.namacabang || 'Nama Cabang Tidak Ditemukan'}
                    action={
                      <IconButton 
                        onClick={() => toggleCabangExpand(key)}
                        sx={{ color: 'white' }}
                      >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    }
                  />
                  
                  <Collapse in={isExpanded}>
                    <CardContent>
                      <StyledTableContainer ref={scrollRef}>
                        <Table stickyHeader sx={{ minWidth: dates.length * 300 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                rowSpan={2}
                                sx={{
                                  fontWeight: 'bold',
                                  backgroundColor: theme.palette.primary.main,
                                  color: 'white',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 3,
                                  borderRight: '1px solid #e0e0e0',
                                }}
                              >
                                Nama
                              </TableCell>
                              {dates.map(date => (
                                <TableCell
                                  key={date}
                                  colSpan={5}
                                  align="center"
                                  sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    borderBottom: '1px solid #e0e0e0',
                                    borderLeft: '1px solid #e0e0e0',
                                  }}
                                >
                                  {new Date(date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                                  <div style={{ fontWeight: 'normal', fontSize: '0.8rem' }}>
                                    {new Date(date).toLocaleDateString('id-ID', { weekday: 'short' })}
                                  </div>
                                </TableCell>
                              ))}
                            </TableRow>
                            <TableRow>
                              {/* <TableCell sx={{ visibility: 'hidden', position: 'sticky', left: 0, zIndex: 2 }}></TableCell> */}
                              {dates.map(date => (
                                <React.Fragment key={date}>
                                  <TableCell align="center" sx={{ backgroundColor: theme.palette.primary.light, color: 'white', fontWeight: 'medium', minWidth: 80, borderRight: '1px solid #fff' }}>Jam Masuk</TableCell>
                                  <TableCell align="center" sx={{ backgroundColor: theme.palette.primary.light, color: 'white', fontWeight: 'medium', minWidth: 80, borderRight: '1px solid #fff' }}>Jam Keluar</TableCell>
                                  <TableCell align="center" sx={{ backgroundColor: theme.palette.primary.light, color: 'white', fontWeight: 'medium', minWidth: 80, borderRight: '1px solid #fff' }}>Total Jam</TableCell>
                                  <TableCell align="center" sx={{ backgroundColor: theme.palette.primary.light, color: 'white', fontWeight: 'medium', minWidth: 80, borderRight: '1px solid #fff' }}>Lembur</TableCell>
                                  <TableCell align="center" sx={{ backgroundColor: theme.palette.primary.light, color: 'white', fontWeight: 'medium', minWidth: 80, borderRight: '1px solid #fff' }}>Status</TableCell>
                                </React.Fragment>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cabang.absensi.reduce((acc, record) => {
                              const existingRow = acc.find(row => row.nama === record.User?.username);
                              if (existingRow) {
                                existingRow.data[record.tgl_absensi] = {
                                  jamMasuk: record.jam_masuk,
                                  jamKeluar: record.jam_keluar
                                };
                              } else {
                                acc.push({
                                  nama: record.User?.username || 'tidak diketahui',
                                  data: {
                                    [record.tgl_absensi]: {
                                      jamMasuk: record.jam_masuk,
                                      jamKeluar: record.jam_keluar
                                    }
                                  }
                                });
                              }
                              return acc;
                            }, []).map((row, index) => (
                              <TableRow key={index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                <StickyTableCell sx={{ fontWeight: 'medium', borderRight: '1px solid #e0e0e0' }}>
                                  {row.nama}
                                </StickyTableCell>
                                {dates.map(date => {
                                  const jamMasuk = row.data[date]?.jamMasuk;
                                  const jamKeluar = row.data[date]?.jamKeluar;
                                  const workingTime = jamMasuk && jamKeluar 
                                    ? hitungJamKerja(jamMasuk, jamKeluar, shiftUntuk(row.nama, date))
                                    : { totalHours: 0, totalMinutes: 0, overtime: { hours: 0, minutes: 0 } };
                                  
                                  return (
                                    <React.Fragment key={date}>
                                      <TableCell align="center">
                                        {jamMasuk || <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>}
                                      </TableCell>
                                      <TableCell align="center">
                                        {jamKeluar || <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>}
                                      </TableCell>
                                      <TableCell align="center">
                                        {jamMasuk && jamKeluar ? (
                                          <Typography variant="body2" color="text.primary">
                                            {`${workingTime.totalHours}:${workingTime.totalMinutes.toString().padStart(2, '0')}`}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {(workingTime.overtime.hours > 0 || workingTime.overtime.minutes > 0) ? (
                                          <Typography variant="body2" color="error.main" sx={{ fontWeight: 'medium' }}>
                                            {`${workingTime.overtime.hours}:${workingTime.overtime.minutes.toString().padStart(2, '0')}`}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {(() => {
                                          const h = petaStatus[row.nama]?.[Number(String(date).slice(8, 10))];
                                          const t = TAMPIL_STATUS[h?.status];
                                          return t ? (
                                            <Tooltip title={teksSel(h)} arrow>
                                              <Typography component="span" variant="body2" sx={{ color: t.warna, fontWeight: 'bold' }}>{t.simbol}</Typography>
                                            </Tooltip>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                                          );
                                        })()}
                                      </TableCell>
                                    </React.Fragment>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </StyledTableContainer>
                      
                      <ExportButtonsContainer>
                        <Tooltip title="Export data ke Excel">
                          <StyledButton
                            variant="contained"
                            color="success"
                            startIcon={<CloudDownloadIcon />}
                            onClick={() => handleExportExcel(key)}
                            disableElevation
                            size={isMobile ? "large" : "medium"}
                          >
                            Export ke Excel
                          </StyledButton>
                        </Tooltip>
                        
                        <Tooltip title="Export data ke PDF">
                          <StyledButton
                            variant="contained"
                            color="error"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => handleExportPDF(key)}
                            disableElevation
                            size={isMobile ? "large" : "medium"}
                          >
                            Export ke PDF
                          </StyledButton>
                        </Tooltip>
                      </ExportButtonsContainer>
                    </CardContent>
                  </Collapse>
                </SubtleCard>
              );
            })}
          </Box>
        )}
      </Paper>
    </ContentContainer>
  );
};

export default DataPresensiBulan;